import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useVertical } from '../contexts/VerticalContext';
import LeaveSection from '../components/LeaveSection';
import LeaveManagement from './LeaveManagement';
import { Calendar, Users, ListTodo, AlertTriangle, TrendingUp, Clock, Target, Award, Edit, Trash, LayoutDashboard, BarChart3, Plus, CheckCircle, ClipboardList, Sparkles, LifeBuoy, ArrowRight, BellRing, UserCheck, Plane, ClipboardCheck, TimerReset, Trophy, Package, User, Inbox } from 'lucide-react';
import GrowthModule from '../components/GrowthModule';
import WorkplaceModule from '../components/WorkplaceModule';
import MyAccountModule from '../components/MyAccountModule';
import Layout from '../components/Layout';
import PayrollManagement from '../components/PayrollManagement';
import TeamSection from '../components/TeamSection';
import TeamStatusBoard from '../components/TeamStatusBoard';
import ReportsHub from './ReportsHub';
import ReportEngine from '../components/ReportEngine';
import HelpdeskModule from '../components/HelpdeskModule';
import ExecutiveAnalyticsDashboard from '../components/ExecutiveAnalyticsDashboard';
import WorkRequestModule from '../components/WorkRequestModule';
import ApprovalsInboxModule from '../components/ApprovalsInboxModule';
import GamificationModuleDeferred from '../components/GamificationModuleDeferred';
import AssetManagementModule from '../components/AssetManagementModule';
import AIAssistant from '../components/AIAssistant';
import ProfileSection from '../components/ProfileSection';
import HRActionCenter from '../components/HRActionCenter';
import HROperationsModule from '../components/HROperationsModule';
import HRLifecycleManagement from '../components/HRLifecycleManagement';
import ExitManagement from '../components/ExitManagement';
import ContractsManagement from '../components/ContractsManagement';
import DocumentTemplates from '../components/DocumentTemplates';
import PulseSurveysModule from '../components/PulseSurveysModule';
import LeaveTypesManagement from '../components/LeaveTypesManagement';
import { format } from 'date-fns';
import Swal from 'sweetalert2';
import { api, attendanceAPI, buildAssetUrl, employeeAPI, leaveAPI } from '../services/api';
import { getManagerNavSchema, mapNavItemsWithIcons, filterAiAssistantNav } from '../lib/navigation';
import { filterNavByModuleAccess } from '../lib/planAccess';
import { hasAiAssistantAddon } from '../lib/aiDashboardAddons';
import { useDashboardTabRoute } from '../hooks/useDashboardTabRoute';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area, LineChart, Line } from 'recharts';
import { CHART_COLORS, chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState, CardSkeleton } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

const ManagerDashboard = () => {
  const { logout, user } = useAuth();
  const { term } = useVertical();
  const [activeTab, setActiveTab] = useDashboardTabRoute('/manager', 'dashboard');
  const navigate = useNavigate();
  // hr_manager shares this console with manager but owns the HR-ops surfaces.
  const isHrManager = String(user?.role || '').toLowerCase() === 'hr_manager';

  const [kpiData, setKpiData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [workloadAnalysis, setWorkloadAnalysis] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opsDashboard, setOpsDashboard] = useState(null);
  const [opsDashboardLoading, setOpsDashboardLoading] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [dashboardTeam, setDashboardTeam] = useState([]);
  const [dashboardLeaves, setDashboardLeaves] = useState([]);
  const [dashboardAttendance, setDashboardAttendance] = useState([]);
  const [dashboardHolidays, setDashboardHolidays] = useState([]);

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    estimated_hours: 0
  });

  const [newShift, setNewShift] = useState({
    name: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    department: '',
    required_employees: 1
  });

  const navItems = useMemo(
    () => {
      const moduleFiltered = filterNavByModuleAccess(
        getManagerNavSchema({ isHrManager }),
        user?.moduleAccess,
        user?.isSuperadmin,
      );
      const filteredSchema = filterAiAssistantNav(moduleFiltered, hasAiAssistantAddon(user));
      return mapNavItemsWithIcons(filteredSchema, {
        dashboard: LayoutDashboard,
        actionCenter: ClipboardList,
        employees: Users,
        onboarding: ClipboardList,
        assets: Package,
        leaves: Calendar,
        finance: BarChart3,
        reports: ClipboardList,
        team: Users,
        helpdesk: LifeBuoy,
        gamification: Trophy,
        teamOps: Users,
        profile: User,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on the specific user.* fields used
    [user?.moduleAccess, user?.isSuperadmin, isHrManager]
  );

  useEffect(() => {
    const allowedIds = new Set(navItems.flatMap((item) => [item.id, ...(item.subItems || []).map((sub) => sub.id)]));
    if (allowedIds.size > 0 && !allowedIds.has(activeTab)) {
      setActiveTab(navItems[0]?.id || 'dashboard');
    }
  }, [navItems, activeTab, setActiveTab]);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchManagerOpsDashboard();
    if (activeTab === 'tasks') fetchTasks();
    if (activeTab === 'schedule') fetchShifts();
    if (activeTab === 'workload') fetchWorkloadAnalysis();
    if (activeTab === 'conflicts') fetchConflicts();
  }, [activeTab]);

  const fetchManagerOpsDashboard = async () => {
    try {
      setOpsDashboardLoading(true);
      const response = await api.get('/api/manager/dashboard');
      setOpsDashboard(response.data);
    } catch (error) {
      console.error('Error fetching manager dashboard:', error);
      setOpsDashboard(null);
    } finally {
      setOpsDashboardLoading(false);
    }
  };

  const fetchKPIData = async () => {
    try {
      const response = await api.get('/api/manager/kpi/dashboard');
      setKpiData(response.data);
    } catch (error) {
      console.error('Error fetching KPI:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await api.get('/api/manager/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchShifts = async () => {
    try {
      const today = new Date();
      const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
      const weekEnd = new Date(today.setDate(today.getDate() + 7));
      
      const response = await api.get('/api/manager/shifts', {
        params: {
          start_date: weekStart.toISOString().split('T')[0],
          end_date: weekEnd.toISOString().split('T')[0]
        }
      });
      setShifts(response.data);
    } catch (error) {
      console.error('Error fetching shifts:', error);
    }
  };

  const fetchWorkloadAnalysis = async () => {
    try {
      const response = await api.get('/api/manager/workload/analysis');
      setWorkloadAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching workload:', error);
    }
  };

  const fetchConflicts = async () => {
    try {
      const response = await api.get('/api/manager/conflicts');
      setConflicts(response.data.conflicts || []);
    } catch (error) {
      console.error('Error fetching conflicts:', error);
    }
  };

  const fetchManagerHomeData = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const [teamRes, leavesRes, holidaysRes, attendanceRes] = await Promise.allSettled([
        employeeAPI.getMyTeam(),
        leaveAPI.list({ status: 'approved' }),
        leaveAPI.getHolidays(currentYear),
        attendanceAPI.getAllRecords(),
      ]);

      setDashboardTeam(teamRes.status === 'fulfilled' ? (teamRes.value.data || []) : []);
      setDashboardLeaves(leavesRes.status === 'fulfilled' ? (leavesRes.value.data || []) : []);
      setDashboardHolidays(holidaysRes.status === 'fulfilled' ? (holidaysRes.value.data || []) : []);
      setDashboardAttendance(attendanceRes.status === 'fulfilled' ? (attendanceRes.value.data || []) : []);
    } catch (error) {
      console.error('Error fetching manager home data:', error);
    }
  };

  const getRecordDateKey = (record) => {
    const candidate =
      record?.date ||
      record?.attendance_date ||
      record?.record_date ||
      record?.check_in_time ||
      record?.check_in ||
      record?.created_at;
    if (!candidate) return null;
    const parsed = new Date(candidate);
    if (Number.isNaN(parsed.getTime())) {
      return String(candidate).slice(0, 10);
    }
    return format(parsed, 'yyyy-MM-dd');
  };

  const getInitials = (name) =>
    String(name || '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');

  const dashboardOverview = useMemo(() => {
    const today = new Date();
    const todayKey = format(today, 'yyyy-MM-dd');
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const teamIds = new Set(dashboardTeam.map((member) => member.employee_id));

    const todayAttendance = dashboardAttendance.filter((record) => {
      const employeeId = record.employee_id || record.employee?.employee_id;
      return teamIds.has(employeeId) && getRecordDateKey(record) === todayKey;
    });

    const presentIds = new Set(todayAttendance.map((record) => record.employee_id || record.employee?.employee_id).filter(Boolean));

    const lateAttendance = todayAttendance.filter((record) => {
      const statusText = String(record.status || record.attendance_status || '').toLowerCase();
      if (statusText.includes('late')) return true;
      if (record.is_late || record.late) return true;
      const checkIn = record.check_in_time || record.check_in;
      if (!checkIn) return false;
      const parsed = new Date(checkIn);
      return !Number.isNaN(parsed.getTime()) && (parsed.getHours() > 9 || (parsed.getHours() === 9 && parsed.getMinutes() > 15));
    });

    const todayLeaves = dashboardLeaves.filter((leave) => {
      const employeeId = leave.employee_id || leave.employee?.employee_id;
      if (!teamIds.has(employeeId)) return false;
      const start = leave.start_date ? new Date(leave.start_date) : null;
      const end = leave.end_date ? new Date(leave.end_date) : null;
      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
      return start <= today && end >= today;
    });

    const monthLeaves = dashboardLeaves.filter((leave) => {
      const employeeId = leave.employee_id || leave.employee?.employee_id;
      if (!teamIds.has(employeeId)) return false;
      const start = leave.start_date ? new Date(leave.start_date) : null;
      if (!start || Number.isNaN(start.getTime())) return false;
      return start >= monthStart && start <= monthEnd;
    });

    const presentTeamMembers = dashboardTeam.filter((member) => presentIds.has(member.employee_id));
    const onTimeMembers = presentTeamMembers.filter((member) => !lateAttendance.some((record) => (record.employee_id || record.employee?.employee_id) === member.employee_id));
    const upcomingHolidays = [...dashboardHolidays]
      .filter((holiday) => {
        const holidayDate = holiday.date ? new Date(holiday.date) : null;
        return holidayDate && !Number.isNaN(holidayDate.getTime()) && holidayDate >= new Date(today.getFullYear(), today.getMonth(), today.getDate());
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 4);

    const pendingReviewCount = (kpiData?.tasks?.pending || 0) + (kpiData?.conflicts?.unresolved || 0);
    const trackingCount = (kpiData?.tasks?.in_progress || 0) + (kpiData?.shifts?.today || 0);

    return {
      pendingReviewCount,
      trackingCount,
      presentTeamMembers,
      onTimeMembers,
      lateAttendance,
      todayLeaves,
      monthLeaves,
      upcomingHolidays,
      notCheckedInCount: Math.max(dashboardTeam.length - presentIds.size - todayLeaves.length, 0),
    };
  }, [dashboardAttendance, dashboardHolidays, dashboardLeaves, dashboardTeam, kpiData]);

  const createTask = async () => {
    try {
      await api.post('/api/manager/tasks/create', newTask);
      Swal.fire({
        icon: 'success',
        title: 'Task Created',
        text: 'The task has been successfully created.',
        timer: 2000,
        showConfirmButton: false
      });
      setNewTask({ title: '', description: '', priority: 'medium', estimated_hours: 0 });
      fetchTasks();
      fetchKPIData();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create task'
      });
    }
  };

  const getTaskRecommendations = async (taskId) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/manager/tasks/${taskId}/recommendations`);
      setRecommendations(response.data.recommendations);
      Swal.fire({
        icon: 'success',
        title: 'Recommendations Ready',
        text: 'AI recommendations generated successfully.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to get recommendations'
      });
    } finally {
      setLoading(false);
    }
  };

  const assignTask = async (taskId, employeeId) => {
    try {
      await api.post(`/api/manager/tasks/${taskId}/assign`, { employee_id: employeeId });
      Swal.fire({
        icon: 'success',
        title: 'Assigned',
        text: 'Task assigned successfully',
        timer: 1500,
        showConfirmButton: false
      });
      fetchTasks();
      setRecommendations([]);
    } catch (error) {
      Swal.fire('Error', 'Failed to assign task', 'error');
    }
  };

  const HR_OPS_TABS = {
    'action-center': <HRActionCenter />,
    'hr-operations': <HROperationsModule />,
    'hr-lifecycle': <HRLifecycleManagement />,
    'exit-management': <ExitManagement />,
    contracts: <ContractsManagement />,
    'doc-templates': <DocumentTemplates />,
    'pulse-surveys': <PulseSurveysModule />,
  };

  const resolveManagerModuleTabs = () => {
    if (isHrManager && HR_OPS_TABS[activeTab]) {
      return HR_OPS_TABS[activeTab];
    }
    if (activeTab === 'ai-assistant-chat') {
      return <AIAssistant />;
    }
    if (activeTab === 'assets') {
      return <AssetManagementModule mode="manager" />;
    }
    if (activeTab === 'leave-approvals-inbox') {
      return <ApprovalsInboxModule />;
    }
    if (activeTab === 'leave-regularization' || activeTab === 'leave-wfh' || activeTab === 'leave-outstation') {
      return (
        <WorkRequestModule
          mode="manager"
          requestType={activeTab === 'leave-regularization' ? 'regularization' : activeTab === 'leave-wfh' ? 'wfh' : 'outstation'}
        />
      );
    }
    if (activeTab.startsWith('growth-')) {
      return <GrowthModule currentTab={activeTab.replace('growth-', '')} />;
    }
    if (activeTab.startsWith('workplace-')) {
      return <WorkplaceModule currentTab={activeTab.replace('workplace-', '')} onNavigate={setActiveTab} />;
    }
    if (activeTab.startsWith('account-')) {
      return <MyAccountModule currentTab={activeTab.replace('account-', '')} />;
    }
    if (activeTab.startsWith('leave-')) {
      if (activeTab === 'leave-types') {
        return <LeaveTypesManagement />;
      }
      if (activeTab === 'leave-requests' || activeTab === 'leave-policies') {
        return <LeaveManagement currentTab={activeTab.replace('leave-', '')} />;
      }
      if (activeTab === 'leave-my-history' || activeTab === 'leave-my-apply' || activeTab === 'leave-my-holidays') {
        return <LeaveSection currentTab={activeTab.replace('leave-my-', '')} />;
      }
      return <LeaveSection currentTab={activeTab.replace('leave-', '')} />;
    }

    if (activeTab.startsWith('finance-')) {
      const financeTabMap = {
        'finance-home': 'overview',
        'finance-payslips': 'payslips',
        'finance-reimbursements': 'reimbursements',
      };
      return <PayrollManagement currentTab={financeTabMap[activeTab] || 'overview'} />;
    }

    if (activeTab.startsWith('report-')) {
      if (activeTab === 'report-center') {
        return <ReportEngine />;
      }
      if (activeTab === 'report-executive') {
        return <ExecutiveAnalyticsDashboard />;
      }
      const reportsTabMap = {
        'report-balance': 'leave_balance',
        'report-statutory': 'statutory_compliance',
        'report-performance': 'performance',
      };
      return <ReportsHub currentTab={reportsTabMap[activeTab] || activeTab.replace('report-', '')} />;
    }

    if (activeTab === 'team') {
      // The board sits above the directory because "who is in today" is the
      // question a manager opens this tab for; it renders nothing at all for
      // somebody with no direct reports, so the tab is unchanged for them.
      return (
        <div className="space-y-4">
          <TeamStatusBoard />
          <TeamSection />
        </div>
      );
    }

    if (activeTab === 'helpdesk') {
      return <HelpdeskModule mode="manager" />;
    }

    if (activeTab.startsWith('gamification-')) {
      return <GamificationModuleDeferred mode="manager" activeTab={activeTab} onNavigate={setActiveTab} />;
    }

    return null;
  };

  const updateTask = async () => {
    if (!editingTask) return;
    try {
      await api.put(`/api/manager/tasks/${editingTask.id}`, editingTask);
      Swal.fire({
        icon: 'success',
        title: 'Task Updated',
        text: 'Task updated successfully',
        timer: 1500,
        showConfirmButton: false
      });
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      Swal.fire('Error', 'Failed to update task', 'error');
    }
  };

  const deleteTask = async (taskId) => {
    const result = await Swal.fire({
      title: 'Delete this task?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'hsl(var(--primary))',
      cancelButtonColor: 'hsl(var(--destructive))',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/manager/tasks/${taskId}`);
        Swal.fire({
            title: 'Deleted!',
            text: 'Task has been deleted.',
            icon: 'success',
            confirmButtonColor: 'hsl(var(--primary))'
        });
        fetchTasks();
      } catch (error) {
        Swal.fire({
            title: 'Error',
            text: 'Failed to delete task',
            icon: 'error',
            confirmButtonColor: 'hsl(var(--primary))'
        });
      }
    }
  };

  const updateShift = async () => {
    if (!editingShift) return;
    try {
      await api.put(`/api/manager/shifts/${editingShift.id}`, editingShift);
      Swal.fire({
        icon: 'success',
        title: 'Shift Updated',
        text: 'Shift updated successfully',
        timer: 1500,
        showConfirmButton: false
      });
      setEditingShift(null);
      fetchShifts();
    } catch (error) {
      Swal.fire('Error', 'Failed to update shift', 'error');
    }
  };

  const deleteShift = async (shiftId) => {
    const result = await Swal.fire({
      title: 'Delete this shift?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'hsl(var(--destructive))',
      cancelButtonColor: 'hsl(var(--muted-foreground))',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/manager/shifts/${shiftId}`);
        Swal.fire('Deleted!', 'Shift has been deleted.', 'success');
        fetchShifts();
      } catch (error) {
        Swal.fire('Error', 'Failed to delete shift', 'error');
      }
    }
  };

  return (
    <Layout 
      items={navItems} 
      activeItem={activeTab} 
      onNavigate={setActiveTab} 
      onLogout={logout}
    >
      <div className="space-y-6" data-testid="manager-dashboard">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-hr-h1 font-bold">Manager Operations Dashboard</h1>
            <p className="text-muted-foreground">AI-powered workforce management & optimization</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/manager')}>
              Back to Main
            </Button>
          </div>
        </div>

        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {opsDashboardLoading && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <CardSkeleton key={i} rows={3} />
                ))}
              </div>
            )}

            {!opsDashboardLoading && opsDashboard && (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  {[
                    { label: 'Total Employees', value: opsDashboard.summary.total_employees, icon: Users, tone: 'primary' },
                    { label: 'Present Today', value: opsDashboard.summary.present_today, icon: UserCheck, tone: 'success' },
                    { label: 'Absent Today', value: opsDashboard.summary.absent_today, icon: AlertTriangle, tone: 'danger' },
                    { label: 'Late Arrivals', value: opsDashboard.summary.late_arrivals, icon: Clock, tone: 'warning' },
                    { label: 'Work From Home', value: opsDashboard.summary.work_from_home, icon: Plane, tone: 'purple' },
                    { label: 'On Leave', value: opsDashboard.summary.on_leave, icon: Calendar, tone: 'info' },
                    { label: 'Attrition Rate', value: opsDashboard.summary.attrition_rate, suffix: '%', icon: TrendingUp, tone: 'warning' },
                    { label: 'Open Positions', value: opsDashboard.summary.open_positions, icon: Target, tone: 'primary' },
                    { label: 'Productivity Score', value: opsDashboard.summary.employee_productivity_score ?? '-', icon: Award, tone: 'success' },
                    { label: 'Satisfaction Score', value: opsDashboard.summary.employee_satisfaction_score ?? '-', icon: Sparkles, tone: 'info' },
                  ].map((item) => (
                    <StatCard
                      key={item.label}
                      label={item.label}
                      value={item.value}
                      suffix={item.suffix || ''}
                      icon={item.icon}
                      tone={item.tone}
                    />
                  ))}
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">{term('department', 'Department')}-wise Distribution</CardTitle>
                    </CardHeader>
                    <CardContent className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={opsDashboard.distribution.department} dataKey="count" nameKey="label" innerRadius={50} outerRadius={90}>
                            {(opsDashboard.distribution.department || []).map((entry, index) => (
                              <Cell key={`${entry.label}-${index}`} fill={chartColor(index)} />
                            ))}
                          </Pie>
                          <RechartsTooltip contentStyle={tooltipContentStyle} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">Location-wise Distribution</CardTitle>
                    </CardHeader>
                    <CardContent className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={opsDashboard.distribution.location}>
                          <CartesianGrid {...gridProps} />
                          <XAxis dataKey="label" {...axisProps} />
                          <YAxis allowDecimals={false} {...axisProps} />
                          <RechartsTooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                          <Bar dataKey="count" fill={chartColor(1)} radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">Employment Type Distribution</CardTitle>
                    </CardHeader>
                    <CardContent className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={opsDashboard.distribution.employment_type} dataKey="count" nameKey="label" outerRadius={95}>
                            {(opsDashboard.distribution.employment_type || []).map((entry, index) => (
                              <Cell key={`${entry.label}-${index}`} fill={chartColor(index)} />
                            ))}
                          </Pie>
                          <RechartsTooltip contentStyle={tooltipContentStyle} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">Daily Attendance Trend (30 Days)</CardTitle>
                    </CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={opsDashboard.attendance.trend_30_days}>
                          <CartesianGrid {...gridProps} />
                          <XAxis dataKey="date" {...axisProps} tick={false} />
                          <YAxis domain={[0, 100]} {...axisProps} />
                          <RechartsTooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                          <Area type="monotone" dataKey="attendance_percent" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.18} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">{term('department', 'Department')}-wise Attendance % (Today)</CardTitle>
                    </CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={opsDashboard.attendance.department_today} layout="vertical">
                          <CartesianGrid {...gridProps} />
                          <XAxis type="number" domain={[0, 100]} {...axisProps} />
                          <YAxis type="category" dataKey="department" width={120} {...axisProps} />
                          <RechartsTooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                          <Bar dataKey="attendance_percent" fill={chartColor(2)} radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">Late Arrival Analysis (Today)</CardTitle>
                    </CardHeader>
                    <CardContent className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            {
                              bucket: 'Today',
                              on_time: opsDashboard.attendance.late_buckets_today.on_time,
                              late_15: opsDashboard.attendance.late_buckets_today.late_15,
                              late_30: opsDashboard.attendance.late_buckets_today.late_30,
                              late_60_plus: opsDashboard.attendance.late_buckets_today.late_60_plus,
                            },
                          ]}
                        >
                          <CartesianGrid {...gridProps} />
                          <XAxis dataKey="bucket" {...axisProps} />
                          <YAxis allowDecimals={false} {...axisProps} />
                          <RechartsTooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                          <Legend />
                          <Bar dataKey="on_time" stackId="a" fill={CHART_COLORS[3]} />
                          <Bar dataKey="late_15" stackId="a" fill={CHART_COLORS[4]} />
                          <Bar dataKey="late_30" stackId="a" fill={CHART_COLORS[7]} />
                          <Bar dataKey="late_60_plus" stackId="a" fill={CHART_COLORS[6]} radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">Leave Trend (Monthly)</CardTitle>
                    </CardHeader>
                    <CardContent className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={opsDashboard.leave.trend_monthly}>
                          <CartesianGrid {...gridProps} />
                          <XAxis dataKey="month" {...axisProps} />
                          <YAxis {...axisProps} />
                          <RechartsTooltip contentStyle={tooltipContentStyle} />
                          <Legend />
                          <Line type="monotone" dataKey="casual" stroke={chartColor(0)} dot={false} />
                          <Line type="monotone" dataKey="sick" stroke={chartColor(1)} dot={false} />
                          <Line type="monotone" dataKey="annual" stroke={chartColor(2)} dot={false} />
                          <Line type="monotone" dataKey="compensatory" stroke={chartColor(3)} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">Leave Utilization by {term('department', 'Department')}</CardTitle>
                    </CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={opsDashboard.leave.utilization_by_department}>
                          <CartesianGrid {...gridProps} />
                          <XAxis dataKey="department" {...axisProps} tick={false} />
                          <YAxis {...axisProps} />
                          <RechartsTooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                          <Legend />
                          <Bar dataKey="allocated" fill={chartColor(1)} radius={[6, 6, 0, 0]} />
                          <Bar dataKey="consumed" fill={chartColor(4)} radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">Upcoming Leave Calendar (30 Days)</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="max-h-80 overflow-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-background">
                            <tr className="border-b">
                              <th className="p-3 text-left">{term('employee', 'Employee')}</th>
                              <th className="p-3 text-left">{term('department', 'Department')}</th>
                              <th className="p-3 text-left">Type</th>
                              <th className="p-3 text-left">Dates</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(opsDashboard.leave.upcoming || []).map((row, idx) => (
                              <tr key={`${row.employee_name}-${idx}`} className="border-b">
                                <td className="p-3">{row.employee_name}</td>
                                <td className="p-3">{row.department}</td>
                                <td className="p-3">{row.leave_type}</td>
                                <td className="p-3">
                                  {row.start_date} → {row.end_date}
                                </td>
                              </tr>
                            ))}
                            {(opsDashboard.leave.upcoming || []).length === 0 && (
                              <tr>
                                <td colSpan={4}>
                                  <EmptyState
                                    compact
                                    icon={Calendar}
                                    title="No upcoming leaves"
                                    description="No approved leaves scheduled in the next 30 days."
                                  />
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {!opsDashboardLoading && !opsDashboard && (
              <Card>
                <CardContent className="p-6">
                  <EmptyState
                    compact
                    icon={Inbox}
                    title="No dashboard data"
                    description="Operations data isn't available right now. Please try again later."
                  />
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {resolveManagerModuleTabs()}

        {/* Content */}
        
        {/* Task Distribution */}
        {activeTab === 'tasks' && (
           <div className="space-y-6 animate-fade-in">
            <Card className="shadow-sm hover:shadow-md border border-border transition-all duration-200">
              <CardHeader className="bg-muted/30 border-b border-border pb-4">
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2 text-foreground">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Target className="h-5 w-5 text-primary" />
                    </div>
                    Task Distribution with AI Recommendations
                  </CardTitle>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all text-primary-foreground">
                        <Plus className="h-4 w-4 mr-2" />
                        Create New Task
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create New Task</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>Title</Label>
                          <Input
                            value={newTask.title}
                            onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                            className="focus-visible:ring-primary transition-colors"
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Input
                            value={newTask.description}
                            onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                            className="focus-visible:ring-primary transition-colors"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Priority</Label>
                            <Select
                              value={newTask.priority}
                              onValueChange={(v) => setNewTask({...newTask, priority: v})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Estimated Hours</Label>
                            <Input
                              type="number"
                              value={newTask.estimated_hours}
                              onChange={(e) => setNewTask({...newTask, estimated_hours: parseFloat(e.target.value)})}
                            />
                          </div>
                        </div>
                        <Button onClick={createTask} className="w-full bg-primary hover:bg-primary/90 active:scale-95 transition-all">Create Task</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {tasks.map((task, index) => (
                    <div 
                      key={task.id} 
                      className="p-4 hover:bg-muted/50 transition-all duration-200 animate-slide-up group"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">{task.title}</h4>
                            <Badge className={`shadow-sm ${
                              task.priority === 'urgent' ? 'bg-destructive/10 text-destructive border-destructive/20' :
                              task.priority === 'high' ? 'bg-[hsl(var(--chart-4))]/10 text-[hsl(var(--chart-4))] border-[hsl(var(--chart-4))]/20' :
                              task.priority === 'medium' ? 'bg-primary/10 text-primary border-primary/20' : 'bg-muted text-muted-foreground border-border'
                            }`}>
                              {task.priority}
                            </Badge>
                            <Badge variant="outline" className="bg-background">{task.status}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
                          <div className="flex gap-4 text-xs text-muted-foreground mt-2 font-medium">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {task.estimated_hours}h</span>
                            {task.assigned_to && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {task.assigned_to}</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 opacity-80 group-hover:opacity-100 transition-opacity">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setEditingTask(task)}
                            className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary hover:border-primary/20"
                            data-testid={`edit-task-${task.id}`}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => deleteTask(task.id)}
                            className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20"
                          >
                            <Trash className="h-3.5 w-3.5" />
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => getTaskRecommendations(task.id)}
                            disabled={loading}
                            className="h-8 text-xs bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 hover:border-primary/30 shadow-sm"
                          >
                            <Award className="h-3 w-3 mr-1" />
                            AI Recommend
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Edit Task Dialog */}
            {editingTask && (
              <Dialog open={!!editingTask} onOpenChange={() => setEditingTask(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Task</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={editingTask.title}
                        onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={editingTask.description || ''}
                        onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Priority</Label>
                        <Select
                          value={editingTask.priority}
                          onValueChange={(v) => setEditingTask({...editingTask, priority: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select
                          value={editingTask.status}
                          onValueChange={(v) => setEditingTask({...editingTask, status: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="assigned">Assigned</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={updateTask} className="w-full">Update Task</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* AI Recommendations Panel */}
            {recommendations.length > 0 && (
              <Card className="border-primary/20 bg-primary/5 animate-fade-in">
                <CardHeader>
                  <CardTitle className="text-primary flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    AI-Powered Assignment Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {recommendations.map((rec, idx) => (
                      <div key={idx} className="p-3 bg-card rounded-lg border border-border hover:border-primary/30 transition-colors flex justify-between items-center shadow-sm">
                        <div className="flex-1">
                          <p className="font-semibold text-foreground">{rec.employee_name}</p>
                          <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                            <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded">Match: {(rec.score * 100).toFixed(0)}%</span>
                            <span>Skills: {(rec.score_breakdown.skill_match * 100).toFixed(0)}%</span>
                            <span>Capacity: {(rec.score_breakdown.capacity_available * 100).toFixed(0)}%</span>
                          </div>
                          <p className="text-xs text-primary/80 mt-1 italic">{rec.recommendation}</p>
                        </div>
                        <Button 
                          size="sm"
                          className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
                          onClick={() => assignTask(recommendations[0]?.task_id || tasks[0]?.id, rec.employee_id)}
                        >
                          Assign
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

          {/* Team Scheduling - Placeholder */}
          {activeTab === 'schedule' && (
            <div className="space-y-6 animate-fade-in">
            <Card className="shadow-md border-border">
              <CardHeader className="bg-muted/30 border-b border-border pb-4">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="p-2 bg-[hsl(var(--chart-2))]/10 rounded-lg">
                    <Calendar className="h-5 w-5 text-[hsl(var(--chart-2))]" />
                  </div>
                  Team Scheduling
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {shifts.map((shift, index) => (
                    <div 
                      key={shift.id} 
                      className="p-4 border border-border rounded-xl hover:shadow-md hover:border-[hsl(var(--chart-2))]/30 transition-all duration-200 animate-slide-up bg-card group"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="p-1.5 bg-[hsl(var(--chart-2))]/10 rounded-md group-hover:bg-[hsl(var(--chart-2))]/20 transition-colors">
                          <Clock className="h-4 w-4 text-[hsl(var(--chart-2))]" />
                        </div>
                        <Badge variant="outline" className="bg-muted border-border group-hover:border-[hsl(var(--chart-2))]/30">{shift.department}</Badge>
                      </div>
                      <h4 className="font-semibold text-foreground mb-1">{shift.name}</h4>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Calendar className="h-3 w-3" /> {shift.date}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                        <Clock className="h-3 w-3" /> {shift.start_time} - {shift.end_time}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Workload Optimization */}
        {activeTab === 'workload' && (
          <div className="space-y-6 animate-fade-in">
          {workloadAnalysis && (
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-t-4 border-t-destructive shadow-md hover:shadow-lg transition-all animate-slide-up" style={{ animationDelay: '0.1s' }}>
                <CardHeader className="bg-destructive/10 pb-4 border-b border-destructive/20">
                  <CardTitle className="text-hr-lg flex items-center gap-2 text-destructive">
                    <div className="p-1.5 bg-destructive/10 rounded-md">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                    </div>
                    Overloaded ({workloadAnalysis.overloaded.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 max-h-[500px] overflow-y-auto">
                  <div className="space-y-3">
                    {workloadAnalysis.overloaded.map((emp, idx) => (
                      <div key={idx} className="p-3 bg-destructive/5 rounded-lg border border-destructive/20 hover:border-destructive/40 transition-colors flex justify-between items-center group">
                        <div>
                          <p className="font-semibold text-foreground">{emp.name}</p>
                          <p className="text-xs text-destructive font-medium flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {emp.utilization.toFixed(0)}% utilized
                          </p>
                        </div>
                        <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center text-destructive text-xs font-bold group-hover:bg-destructive/20 transition-colors">
                          {emp.utilization.toFixed(0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

                <Card className="border-t-4 border-t-[hsl(var(--chart-2))] shadow-md hover:shadow-lg transition-all animate-slide-up" style={{ animationDelay: '0.2s' }}>
                  <CardHeader className="bg-[hsl(var(--chart-2))]/10 pb-4 border-b border-[hsl(var(--chart-2))]/20">
                    <CardTitle className="text-hr-lg flex items-center gap-2 text-[hsl(var(--chart-2))]">
                      <div className="p-1.5 bg-[hsl(var(--chart-2))]/10 rounded-md">
                        <CheckCircle className="h-4 w-4 text-[hsl(var(--chart-2))]" />
                      </div>
                      Optimal ({workloadAnalysis.optimal.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 max-h-[500px] overflow-y-auto">
                    <div className="space-y-3">
                      {workloadAnalysis.optimal.slice(0, 10).map((emp, idx) => (
                        <div key={idx} className="p-3 bg-[hsl(var(--chart-2))]/5 rounded-lg border border-[hsl(var(--chart-2))]/20 hover:border-[hsl(var(--chart-2))]/40 transition-colors flex justify-between items-center group">
                          <div>
                            <p className="font-semibold text-foreground">{emp.name}</p>
                            <p className="text-xs text-[hsl(var(--chart-2))] font-medium flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              {emp.utilization.toFixed(0)}% utilized
                            </p>
                          </div>
                          <div className="h-8 w-8 rounded-full bg-[hsl(var(--chart-2))]/10 flex items-center justify-center text-[hsl(var(--chart-2))] text-xs font-bold group-hover:bg-[hsl(var(--chart-2))]/20 transition-colors">
                            {emp.utilization.toFixed(0)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-t-4 border-t-[hsl(var(--chart-4))] shadow-md hover:shadow-lg transition-all animate-slide-up" style={{ animationDelay: '0.3s' }}>
                  <CardHeader className="bg-[hsl(var(--chart-4))]/10 pb-4 border-b border-[hsl(var(--chart-4))]/20">
                    <CardTitle className="text-hr-lg flex items-center gap-2 text-[hsl(var(--chart-4))]">
                      <div className="p-1.5 bg-[hsl(var(--chart-4))]/10 rounded-md">
                        <Clock className="h-4 w-4 text-[hsl(var(--chart-4))]" />
                      </div>
                      Underutilized ({workloadAnalysis.underutilized.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 max-h-[500px] overflow-y-auto">
                    <div className="space-y-3">
                      {workloadAnalysis.underutilized.map((emp, idx) => (
                        <div key={idx} className="p-3 bg-[hsl(var(--chart-4))]/5 rounded-lg border border-[hsl(var(--chart-4))]/20 hover:border-[hsl(var(--chart-4))]/40 transition-colors flex justify-between items-center group">
                          <div>
                            <p className="font-semibold text-foreground">{emp.name}</p>
                            <p className="text-xs text-[hsl(var(--chart-4))] font-medium flex items-center gap-1">
                              <TrendingUp className="h-3 w-3 rotate-180" />
                              {emp.utilization.toFixed(0)}% utilized
                            </p>
                          </div>
                          <div className="h-8 w-8 rounded-full bg-[hsl(var(--chart-4))]/10 flex items-center justify-center text-[hsl(var(--chart-4))] text-xs font-bold group-hover:bg-[hsl(var(--chart-4))]/20 transition-colors">
                            {emp.utilization.toFixed(0)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

          {/* Conflict Resolution */}
          {activeTab === 'conflicts' && (
            <div className="space-y-6 animate-fade-in">
            <Card className="shadow-md border-border">
              <CardHeader className="bg-muted/30 border-b border-border pb-4">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="p-2 bg-[hsl(var(--chart-3))]/10 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-[hsl(var(--chart-3))]" />
                  </div>
                  Scheduling Conflicts
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 gap-4">
                  {conflicts.length > 0 ? conflicts.map((conflict, idx) => (
                    <div 
                      key={idx} 
                      className="p-4 border-l-4 border-l-[hsl(var(--chart-3))] bg-card rounded-r-lg border-y border-r border-border shadow-sm hover:shadow-md transition-all duration-200 animate-slide-up group"
                      style={{ animationDelay: `${idx * 0.05}s` }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle className="h-4 w-4 text-[hsl(var(--chart-3))]" />
                            <p className="font-bold text-foreground">{conflict.type.replace('_', ' ').toUpperCase()}</p>
                            <Badge className="bg-[hsl(var(--chart-3))]/10 text-[hsl(var(--chart-3))] hover:bg-[hsl(var(--chart-3))]/20 border-[hsl(var(--chart-3))]/20 ml-2">{conflict.severity}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{conflict.description}</p>
                          <div className="flex items-center gap-2 mt-3">
                            <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                              <Users className="h-3 w-3 mr-1" />
                              ID: {conflict.employee_id}
                            </Badge>
                            <Button size="sm" variant="outline" className="h-6 text-xs border-[hsl(var(--chart-3))]/20 text-[hsl(var(--chart-3))] hover:bg-[hsl(var(--chart-3))]/10">
                              Resolve
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <EmptyState
                      icon={CheckCircle}
                      title="No conflicts detected"
                      description="Everything is running smoothly!"
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'profile' && <ProfileSection />}
      </div>
    </Layout>
  );
};

export default ManagerDashboard;
